/**
 * Detects the current execution environment.
 * Run once at bootstrap; pass the result to containers and consent resolvers.
 *
 * isFriendlyIframe — same-origin all the way to window.top; JS can walk freely
 * canAccessParent  — window.parent is accessible (may be false mid cross-origin chain)
 * frameDepth       — number of frames between this window and window.top
 *                    (computed even cross-origin; parent/top are safelisted)
 * isSafeFrame      — tag is running inside a SafeFrame managed by an ad server ($sf.ext)
 * currentScript    — the <script> element that loaded this tag (null only for module scripts)
 *                    (must be captured at top level during initial execution)
 */

/** Backstop for the frame-depth walk against pathological frame nesting. */
const FRAME_DEPTH_WALK_CAP = 25;

export function getRenderEnv() {
  // MRAID is authoritative even when the SDK's WebView happens to expose a
  // top-level browser window. Treating it as an ordinary page would advertise
  // DOM resize capabilities the native host does not provide.
  const isMraid = typeof window.mraid !== 'undefined';
  const inIframe = window.self !== window.top;
  let isFriendlyIframe = false;
  let canAccessParent  = false;
  let frameDepth       = 0;
  let topWindow        = null;
  let frameElement     = null;

  if (inIframe) {
    // window.parent / .top are safelisted across origins — this walk never
    // throws, so depth is known even from inside a cross-origin frame.
    try {
      let win = window;
      while (win !== win.top && frameDepth < FRAME_DEPTH_WALK_CAP) {
        win = win.parent;
        frameDepth++;
      }
    } catch (_) {
      // Never observed in practice; keep whatever depth was reached.
    }

    // The safelisted walk can't tell same-origin from cross-origin. Probe
    // `.document` at every step (not safelisted) — it throws SecurityError the
    // moment we hit a cross-origin frame. Only mark isFriendlyIframe after
    // every frame up to window.top has cleared the probe.
    try {
      let win = window.parent;
      void win.document;
      while (win !== win.top) {
        win = win.parent;
        void win.document;
      }
      void window.top.document;
      isFriendlyIframe = true;
      canAccessParent  = true;
      topWindow        = window.top;
      frameElement     = window.frameElement;
    } catch (_) {
      try { void window.parent.document; canAccessParent = true; } catch (_) {}
    }
  }

  return {
    inIframe,
    isFriendlyIframe,
    canAccessParent,
    frameDepth,
    isMraid,
    isSafeFrame: typeof window.$sf !== 'undefined' && typeof window.$sf?.ext !== 'undefined',
    topWindow,
    frameElement,
    currentScript: document.currentScript ?? null,
  };
}

/**
 * Returns a known-good page URL, or null if one cannot be confirmed.
 *
 * Three trusted sources, in priority order:
 *   1. window.top.location.href — only attempted when same-origin access is
 *      already confirmed (not in iframe, or friendly iframe). Never tried
 *      cross-origin — it would throw and tell us nothing useful.
 *   2. window.__KNOWN_PAGE_URL__ — set by the ad-server creative snippet via a
 *      GAM %%PATTERN:url%% macro. Normalized and validated: unexpanded macros
 *      (any syntax) and non-http(s) values are rejected, GAM's escaped form is
 *      decoded once. See normalizeKnownPageUrl.
 *   3. document.referrer — only when it is provably the complete top-page URL.
 *      See confirmedReferrerUrl for the predicate.
 *
 * Absent = unknown origin. The server falls back to the property domain only.
 *
 * @param {ReturnType<typeof getRenderEnv>} renderEnv
 * @returns {string|null}
 */
export function getPageUrl(renderEnv) {
  // Source 1: direct top-frame access — confirmed safe by isFriendlyIframe check
  if (!renderEnv.inIframe || renderEnv.isFriendlyIframe) {
    try {
      const url = window.top.location.href;
      if (url) return url;
    } catch (_) {
      // Shouldn't happen given our renderEnv check, but never trust the browser
    }
  }

  // Source 2: ad-server macro expanded at serve time (GAM %%PATTERN:url%%)
  const macro = normalizeKnownPageUrl(window.__KNOWN_PAGE_URL__);
  if (macro) return macro;

  // Source 3: referrer, only when provably the complete top-page URL
  return confirmedReferrerUrl(renderEnv);
}

/**
 * Normalizes and validates a publisher/ad-server supplied page URL.
 *
 * Accepts only values that parse as an http(s) URL. Values still containing
 * `%%` are unexpanded GAM macros and are rejected outright. GAM's
 * %%PATTERN:url%% escapes unsafe characters, so a fully escaped value
 * (`https%3A%2F%2F...`) fails the first parse — decode exactly once and retry.
 * A value that parsed raw is never decoded, so already-escaped query params
 * survive untouched.
 *
 * @param {unknown} raw - window.__KNOWN_PAGE_URL__ as provided
 * @returns {string|null} normalized absolute URL, or null if not trustworthy
 */
export function normalizeKnownPageUrl(raw) {
  if (typeof raw !== 'string' || raw.length === 0 || raw.includes('%%')) return null;

  const direct = parseHttpUrl(raw);
  if (direct) return direct.href;

  try {
    const decoded = parseHttpUrl(decodeURIComponent(raw));
    if (decoded) return decoded.href;
  } catch (_) {
    // Malformed percent-escapes — junk, not a URL
  }

  return null;
}

/**
 * Returns document.referrer when it is provably the complete URL of the top
 * page, else null.
 *
 * Referrer policies can only produce three shapes: empty, bare origin, or the
 * COMPLETE referring URL — there is no partial-path policy. So a non-root path
 * or query proves the value was not policy-trimmed. The remaining question is
 * whether the referring document is the top page:
 *   - frameDepth === 1: our parent IS window.top, so the document that
 *     navigated us is the top page (SafeFrames sit at depth 1).
 *   - referrer origin !== our own origin: rejects self-navigations inside the
 *     frame, whose referrer is the frame's previous document, not the page.
 *   - location.ancestorOrigins must attest that the referrer origin IS the
 *     top page. This is REQUIRED, not best-effort: without it a client-side
 *     cross-origin redirect chain inside the frame leaves a depth-1-shaped
 *     foreign referrer we cannot tell apart from the top page. Engines
 *     without ancestorOrigins (Firefox) never use the referrer source —
 *     measured behavior shows Firefox trims cross-origin referrers to bare
 *     origin regardless of the element's referrerpolicy, so the attestation
 *     requirement costs no real coverage there anyway.
 *
 * Bare-origin referrers are always rejected: under the default
 * strict-origin-when-cross-origin policy a trimmed referrer is
 * indistinguishable from the user genuinely being on the homepage.
 *
 * @param {ReturnType<typeof getRenderEnv>} renderEnv
 * @returns {string|null}
 */
function confirmedReferrerUrl(renderEnv) {
  if (!renderEnv.inIframe || renderEnv.isFriendlyIframe || renderEnv.frameDepth !== 1) {
    return null;
  }

  const ref = parseHttpUrl(document.referrer);
  if (!ref) return null;

  if (ref.origin === window.location.origin) return null;

  if (ref.pathname === '/' && ref.search === '') return null;

  const ancestors = window.location.ancestorOrigins;
  if (!ancestors || ancestors.length === 0 || ref.origin !== ancestors[ancestors.length - 1]) {
    return null;
  }

  return ref.href;
}

/**
 * Parses a string as an absolute http(s) URL.
 *
 * @param {unknown} value
 * @returns {URL|null}
 */
function parseHttpUrl(value) {
  if (typeof value !== 'string' || value.length === 0) return null;
  try {
    const url = new URL(value);
    return (url.protocol === 'https:' || url.protocol === 'http:') ? url : null;
  } catch (_) {
    return null;
  }
}

/**
 * Whether this execution context can directly inspect and gate publisher DOM.
 * Surface sizing and expansion belong exclusively to SurfaceController; this
 * narrower predicate exists only for containers such as ContentLock.
 *
 * @param {ReturnType<typeof getRenderEnv>} renderEnv
 * @returns {boolean}
 */
export function hasPublisherDocumentAccess(renderEnv) {
  return !renderEnv.isMraid
    && !renderEnv.isSafeFrame
    && !renderEnv.inIframe;
}
